import React from 'react'

import MoveGroupModal, { MoveGroupState } from './MoveGroupModal'
import {
  BoardGroupFragment,
  PlanGroupsDocument,
  useMovePlanGroupMutation,
  useMoveTemplateGroupMutation,
  usePlanGroupsQuery
} from '../../../../generated/graphql'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'
import { updateMovePlanGroupCache } from '../../../../lib/cacheUtils'

type MoveGroupModalWrapperProps = {
  /** Current Plan ID */
  planId: string
  /** Current Plan Group ID */
  groupId: string
  /** Indicates if the modal is open */
  isOpen: boolean
  /** Indicates if this is a template */
  isTemplate: boolean
}

const MoveGroupModalWrapper = ({
  planId,
  groupId,
  isOpen,
  isTemplate
}: MoveGroupModalWrapperProps) => {
  const { hideModal } = useModal()
  const { showErrorNotif } = useNotification()
  const groupsQuery = usePlanGroupsQuery({
    variables: { planId },
    onError: () => {
      showErrorNotif('An error occurred. Please refresh page.')
    }
  })
  const [moveGroup] = useMovePlanGroupMutation({
    onCompleted: () => hideModal(),
    onError: () => showErrorNotif('An error occurred. Please refresh page.')
  })

  const [moveTemplateGroup] = useMoveTemplateGroupMutation({
    onCompleted: () => hideModal(),
    onError: () => showErrorNotif('An error occurred. Please refresh page.')
  })

  if (groupsQuery.loading) {
    return null
  }

  const groups = groupsQuery.data?.planGroups || []

  const handleCancel = () => {
    hideModal()
  }

  const handleSave = ({ selectedGroupId, destinationType, destinationGroupId }: MoveGroupState) => {
    const selectedGroup = groups.find((g: BoardGroupFragment) => g.id === selectedGroupId)
    const destGroup = groups.find((g: BoardGroupFragment) => g.id === destinationGroupId)

    if (selectedGroup && destGroup && selectedGroup.id !== destGroup.id) {
      let offset = 0

      if (destinationType === 'BEFORE') {
        offset = destGroup.displaySequence < selectedGroup.displaySequence ? 0 : -1
      } else if (destinationType === 'AFTER') {
        offset = destGroup.displaySequence < selectedGroup.displaySequence ? 1 : 0
      }

      const displaySequence = destGroup.displaySequence + offset

      if (isTemplate) {
        moveTemplateGroup({
          variables: {
            input: {
              id: selectedGroupId,
              displaySequence
            }
          },
          refetchQueries: [
            // Because of the re-sequencing that happens when a different term option is selected,
            // refetch groups so the titles are accurate.  The logic to re-sequence is complicated
            // enough that it should stay in the backend.
            {
              query: PlanGroupsDocument,
              variables: { planId }
            }
          ],
          update: (proxy, { data }) =>
            updateMovePlanGroupCache(
              proxy,
              planId,
              data?.moveTemplateGroup as BoardGroupFragment,
              groups
            ),
          optimisticResponse: {
            __typename: 'Mutation',
            moveTemplateGroup: {
              __typename: 'PlanGroup',
              id: selectedGroupId,
              planId,
              displaySequence
            }
          }
        })
      } else {
        moveGroup({
          variables: {
            input: {
              id: selectedGroupId,
              displaySequence
            }
          },
          update: (proxy, { data }) =>
            updateMovePlanGroupCache(
              proxy,
              planId,
              data?.movePlanGroup as BoardGroupFragment,
              groups
            ),
          optimisticResponse: {
            __typename: 'Mutation',
            movePlanGroup: {
              __typename: 'PlanGroup',
              id: selectedGroupId,
              planId,
              displaySequence
            }
          }
        })
      }
    }
  }

  return (
    <MoveGroupModal
      groupId={groupId}
      groups={groups}
      isOpen={isOpen}
      onCancel={handleCancel}
      onSave={handleSave}
    />
  )
}

export default MoveGroupModalWrapper
